import { Suspense, useCallback, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import { routes } from "./routes/routes.jsx";
import ProtectedRoute from "./routes/ProtectedRoute";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import Container from "./components/layout/Container";
import { BottomBar, Drawer } from "./components/layout/MobileNav";
import Sidebar from "./components/layout/Sidebar";
import ErrorBoundary, { RouteErrorBoundary } from "./components/ErrorBoundary";
import { useAuth, isCustomer } from "./hooks/useAuth";
import { PUBLIC_AUTH_PATHS } from "./lib/api";

// The layout shell — template §3: "App.jsx — layout shell + <Outlet/> + one <Toaster/>".
//
// Routes are rendered from the single route array rather than written out here, so adding a page is
// a one-line change in `routes/routes.jsx`.
export default function App() {
  const [menuOpen, setMenuOpen] = useState(false); // the phone's "More" drawer
  const [sidebarExpanded, setSidebarExpanded] = useState(false); // the tablet rail (CS-2, owner D-1)
  // Stable, so the drawer's focus handling runs once per opening — not on every re-render while open.
  const closeMore = useCallback(() => setMenuOpen(false), []);
  // Phase 8 follow-up (owner ruling RB-3; MDS §37.17): the portal chrome belongs to a CUSTOMER session only. Any other
  // session (the admin panel shares the API session) is treated as signed out here — and is never cleared.
  const { user: sessionUser, mustChangePassword } = useAuth();
  const user = isCustomer(sessionUser) ? sessionUser : null;
  const location = useLocation();

  // The sign-in page gets no chrome: a header full of destinations you cannot reach is noise.
  //
  // A FORCED PASSWORD CHANGE IS THE SAME SITUATION. The chrome lives outside ProtectedRoute, so
  // without this the gate would render inside a header and bottom bar whose every link leads back to
  // the gate — and the header's unread badge polls `/notifications/unread-count` every 60s, which
  // `requirePasswordChanged` answers 403 until the password is set.
  // CS-5A: every signed-out credential page is bare too (the list lives with the API client that exempts them).
  // Phase 8 (BD-3, UI-2(b); MDS §37.4): `/` with nobody signed in is the public Home, which brings its own public
  // header and footer — never the portal chrome.
  const bare = PUBLIC_AUTH_PATHS.includes(location.pathname) || mustChangePassword || (location.pathname === "/" && !user);

  return (
    <div className="vp-shell">
      {/* THE LAST RESORT (H-4). The route-level boundary below keeps the chrome for a page crash;
          this one exists for what that cannot catch — the header, the drawer or the bottom bar
          itself failing to render. */}
      <ErrorBoundary>
      {/* Part 6 (2026-10-07; owner Part 6 accessibility): the first focusable element — past the header and navigation. */}
      <a href="#main" className="visually-hidden-focusable vp-skip-link">Skip to main content</a>
      {!bare && user && <Header />}
      {!bare && user && <Drawer open={menuOpen} onClose={closeMore} />}
      {!bare && user && (
        <Sidebar expanded={sidebarExpanded} onToggle={() => setSidebarExpanded((v) => !v)}
          onNavigate={() => setSidebarExpanded(false)} />
      )}

      <div className={bare || !user ? "vp-content-bare" : "vp-content"}>
      <main id="main" tabIndex={-1} className={bare ? "" : "vp-main"}>
        {/* A page that throws is contained here (H-4): the header and bottom bar stay mounted, so the
            customer can tap elsewhere instead of reloading. It wraps the Suspense rather than sitting
            inside it, so a REJECTED lazy import — a stale chunk after a deploy — is caught too.
            Keyed by pathname, so navigation clears the error automatically. */}
        <RouteErrorBoundary>
        {/* ONE Suspense boundary at the root. Per-route boundaries would flash a fallback on every
            navigation; one boundary shows it only while an undownloaded chunk loads. */}
        <Suspense
          fallback={
            <div className="text-center text-muted py-5">
              <span className="spinner-border spinner-border-sm me-2" />
              Loading…
            </div>
          }
        >
          <Routes>
            {routes.map(({ path, element: Element, protected: isProtected, signedOut: SignedOut }) => (
              <Route
                key={path}
                path={path}
                element={
                  isProtected ? (
                    <ProtectedRoute signedOut={SignedOut ? <SignedOut /> : null}>
                      <Container className="py-3 py-md-4">
                        <Element />
                      </Container>
                    </ProtectedRoute>
                  ) : (
                    <Element />
                  )
                }
              />
            ))}
          </Routes>
        </Suspense>
        </RouteErrorBoundary>
      </main>

      {!bare && <Footer />}
      </div>
      {!bare && user && <BottomBar moreOpen={menuOpen} onOpenMore={() => setMenuOpen(true)} />}
      </ErrorBoundary>

      {/* Outside the boundary, so a toast can still be shown while the fallback is displayed. */}
      <Toaster position="top-center" toastOptions={{ style: { fontSize: 14 } }} />
    </div>
  );
}
